import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react'
import {KTSVG} from '../../../../_metronic/helpers/components/KTSVG'
import Tooltip from 'react-bootstrap/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Select from 'react-select'
import {Link} from 'react-router-dom'
import {Form, Formik} from 'formik'
import axios from 'axios'
import toaster from '../../../../Utils/toaster'
import {Modal} from 'bootstrap'
import * as Yup from 'yup'
import {CommonPaginationModel} from '../../../../Utils/commonPagination'
import {useDownloadExcel, downloadExcel} from 'react-export-table-to-excel'
import {BillingInterpreterFilterDropdown} from './BillingInterpreterFilterDropdown'
import {InterpreterVoucherTable} from './InterpreterVoucherViewTable'
import {useAuth} from '../../../modules/auth'
import {useDispatch, useSelector} from 'react-redux'
import {
  InterpreterInvoiceCurrentPage,
  InterpreterInvoiceSearch,
  InterpreterInvoiceRowsPerPage,
} from '../../../redux/tableSlice/tableSlice'
import {useInterpreters, useInvoiceTerms, useInvoiceStatus} from '../../../hooks/useGlobalData'
const moment = require('moment-timezone')

const API_URL = process.env.REACT_APP_API_URL

const createInvoicesSchema = Yup.object()
  .shape({
    from: Yup.date().required('Required').typeError('Invalid date'),
    to: Yup.date()
      .required('Required')
      .typeError('Invalid date')
      .when('from', (from, schema) => {
        return from
          ? schema.min(from, 'To date must be greater than or equal to From date')
          : schema
      }),
    interpreters: Yup.array().required('Required').min(1, 'Please select at least one customer'),
    term: Yup.string().required('Required'),
    voucherDate: Yup.date().required('Required').typeError('Invalid date'),
    isIncludeCalls: Yup.boolean(),
    isIncludeAppointments: Yup.boolean(),
    isIndividualInvoice: Yup.boolean(),
    fK_TimeZoneCode: Yup.string().required('Required'),
  })
  .test('at-least-one-true', function (value) {
    const {isIncludeCalls, isIncludeAppointments} = value || {}

    if (!isIncludeCalls && !isIncludeAppointments) {
      return this.createError({
        path: 'isIncludeCalls', // You can choose where to attach the error
        message: 'Please select at least one of Include Calls or Include Appointments',
      })
    }

    return true
  })

const customStyles = {
  control: (provided: any) => ({
    ...provided,
    width: '100%',
    border: '1px solide4e6ef',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    color: '#a4afc4',
  }),
}

// ── Bulk‑job types ──────────────────────────────────────────
interface ActiveVoucherJob {
  code: number
  status: string
  totalInterpreters: number
  processedCount: number
  successCount: number
  skippedCount: number
  failedCount: number
  totalLinesCreated: number
  periodFrom: string
  periodTo: string
  requestedByName: string
  createdDateTime: string
  startedDateTime: string | null
  completedDateTime: string | null
}

const POLL_INTERVAL = 10_000 // 10 seconds

/* eslint-disable jsx-a11y/anchor-is-valid */
export function InterpreterInvoice() {
  const dispatch = useDispatch()
  const tableRef = useRef<any>(null)
  const {currentUser} = useAuth()
  const isSystemAdmin = currentUser?.result?.userType === 'SYSTEM'
  const inputRef = useRef<HTMLInputElement>(null)
  const [allAccounts, setAllAccounts] = useState<any[]>([])
  const [terms, setTerms] = useState<any[]>([])
  const [allStatus, setAllStatus] = useState<any[]>([])
  const [initialValues, setInitialValues] = useState({
    from: '',
    to: moment().format('YYYY-MM-DD'),
    interpreters: [],
    term: '',
    voucherDate: moment().format('YYYY-MM-DD'),
    isIncludeCalls: false,
    isIncludeAppointments: false,
    isIndividualInvoice: false,
    fK_TimeZoneCode: '',
  })
  const [isLoading, setIsLoading] = useState(true)
  const [customerInvoices, setCustomerInvoices] = useState([])
  const [unavailableRates, setUnavailableRates] = useState([])
  const [timezoneOptions, setTimeZoneOptions] = useState<any[]>([])

  // ── Bulk‑job state ──────────────────────────────────────
  const [activeJob, setActiveJob] = useState<ActiveVoucherJob | null>(null)
  const [doneDismissed, setDoneDismissed] = useState(false)
  const [guardOpen, setGuardOpen] = useState(false)
  const [checkingStatus, setCheckingStatus] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const isJobRunning =
    isSystemAdmin &&
    activeJob != null &&
    (activeJob.status === 'Queued' || activeJob.status === 'Processing')
  const isJobDone =
    isSystemAdmin &&
    activeJob != null &&
    (activeJob.status === 'Completed' || activeJob.status === 'Failed') &&
    !doneDismissed

  const fetchActiveJob = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/invoice/voucher/job/active`)
      const job = res.data?.result as ActiveVoucherJob | null
      setActiveJob(job)
      if (job && (job.status === 'Completed' || job.status === 'Failed')) {
        setDoneDismissed(false)
      }
      return job
    } catch {
      return null
    }
  }, [])

  const handleCheckBulkJobStatus = useCallback(async () => {
    setCheckingStatus(true)
    try {
      const job = await fetchActiveJob()
      const isIncomplete =
        job != null &&
        (job.status === 'Queued' || job.status === 'Processing') &&
        job.processedCount < job.totalInterpreters

      if (isIncomplete) {
        try {
          const resumeRes = await axios.post(
            `${API_URL}/invoice/voucher/job/${job.code}/resume`
          )
          const enqueued = resumeRes.data?.result?.enqueued === true
          const message =
            resumeRes.data?.text ||
            resumeRes.data?.result?.message ||
            (enqueued ? 'Bulk run resumed.' : 'Could not resume the bulk run.')

          if (enqueued) {
            toaster('success', message)
          } else if (resumeRes.data?.status === 'S') {
            toaster('success', message)
          } else {
            toaster('info', message)
          }
        } catch {
          toaster('error', 'Could not resume the bulk run. Status was refreshed.')
        }
      }

      await fetchActiveJob()
    } catch {
      toaster('error', 'Could not check bulk run status.')
    } finally {
      setCheckingStatus(false)
    }
  }, [fetchActiveJob])

  // Start / stop polling
  useEffect(() => {
    if (isJobRunning) {
      if (!pollRef.current) {
        pollRef.current = setInterval(fetchActiveJob, POLL_INTERVAL)
      }
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [isJobRunning, fetchActiveJob])

  useEffect(() => {
    if (isSystemAdmin) {
      fetchActiveJob()
    } else {
      setActiveJob(null)
      setDoneDismissed(false)
    }
  }, [fetchActiveJob, isSystemAdmin])

  const formatJobPeriod = (job: ActiveVoucherJob) => {
    const from = moment(job.periodFrom).format('MMM D')
    const to = moment(job.periodTo).format('MMM D, YYYY')
    return `${from} – ${to}`
  }

  const formatJobStarted = (job: ActiveVoucherJob) => {
    if (!job.startedDateTime) return 'Queued'
    return moment(job.startedDateTime).format('MM/DD/YYYY hh:mm A')
  }

  // pagination
  const {currentPage, rowsPerPage, searchQuery, filterData} = useSelector((state: any) => ({
    currentPage: state.table.InterperterInvoice[0]?.currentPage,
    rowsPerPage: state.table.InterperterInvoice[0]?.rowsPerPage,
    searchQuery: state.table.InterperterInvoice[0]?.searchQuery,
    filterData: state.table.InterperterInvoice[0]?.filterData,
  }))
  const [totalPages, setTotalPages] = useState(0)
  const [totalItems, setTotalItems] = useState(0)

  const fetchCustomerInvoice = async (
    passingData: any,
    searchQuery: any,
    currentPage: number,
    rowsPerPage: number
  ) => {
    setIsLoading(true)
    try {
      let response = await axios.post(
        `${API_URL}/invoice/overview-filter`,
        {
          ...passingData,
          fromDate: passingData.fromDate === '' ? null : passingData.fromDate,
          company:
            passingData.company?.length > 0
              ? passingData.company.map((d: any) => d.value.toString())
              : [],
          status: passingData.status?.length > 0 ? passingData.status.map((d: any) => d.value) : [],
          filterType: 'V',
        },
        {
          params: {
            page: currentPage,
            items_per_page: rowsPerPage,
            ...(searchQuery.length > 0 ? {search: searchQuery} : searchQuery),
          },
        }
      )
      const {data, payload} = response.data
      setCustomerInvoices(data || [])
      setTotalPages(payload?.pagination?.last_page || 0)
      setTotalItems(payload?.pagination?.total || 0)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const searchValue = inputRef.current?.value || ''
      dispatch(InterpreterInvoiceSearch(searchValue))
      dispatch(InterpreterInvoiceCurrentPage(1))
      fetchCustomerInvoice(filterData, {search: searchValue}, 1, rowsPerPage)
    }
  }

  const hideModal = (elementID: string) => {
    const modal = document.getElementById(elementID)
    if (modal) {
      const modalInstance = Modal.getInstance(modal)
      if (modalInstance) modalInstance.hide()
    }
  }

  const showModal = (elementID: string) => {
    const modal = document.getElementById(elementID)
    if (modal) {
      const modalInstance = Modal.getInstance(modal) || new Modal(modal)
      modalInstance.show()
    }
  }

  // Use global cached data for interpreters, terms, and status
  const {data: interpretersData} = useInterpreters()
  const {data: termsData} = useInvoiceTerms()
  const {data: statusData} = useInvoiceStatus()

  // Memoized options from global data
  const globalInterpreterOptions = useMemo(() =>
    interpretersData?.map((d: any) => ({value: d.key ?? '', label: d.value ?? ''})) ?? []
  , [interpretersData])

  const globalTermsOptions = useMemo(() =>
    termsData?.map((d: any) => ({value: d.key ?? '', label: d.value ?? ''})) ?? []
  , [termsData])

  const globalStatusOptions = useMemo(() => {
    if (currentUser?.result.userType === 'INTERPRETER' || currentUser?.result.userType === 'CONSUMER') {
      return statusData?.filter((d: any) => d.identification !== 'DR' && d.identification !== 'VO')
        .map((d: any) => ({value: d.key ?? '', label: d.value ?? ''})) ?? []
    }
    return statusData?.map((d: any) => ({value: d.key ?? '', label: d.value ?? ''})) ?? []
  }, [statusData, currentUser?.result.userType])

  // Sync global data with local state
  useEffect(() => {
    if (globalInterpreterOptions.length > 0) setAllAccounts(globalInterpreterOptions)
  }, [globalInterpreterOptions])

  useEffect(() => {
    if (globalTermsOptions.length > 0) setTerms(globalTermsOptions)
  }, [globalTermsOptions])

  useEffect(() => {
    if (globalStatusOptions.length > 0) setAllStatus(globalStatusOptions)
  }, [globalStatusOptions])

  const fetchInterperters = async () => {
    // Now using global cached data - this function is kept for backward compatibility
    if (globalInterpreterOptions.length === 0) {
      try {
        let response = await axios.get(`${API_URL}/accounts/dd-list-accounts/INTERPRETER`)
        setAllAccounts(
          response?.data?.data.map((d: {key: string; value: string}) => ({
            value: d.key ?? '',
            label: d.value ?? '',
          })) ?? []
        )
      } catch (error) {
        console.error(error)
      }
    }
  }

  const fetchTerms = async () => {
    // Now using global cached data - this function is kept for backward compatibility
    if (globalTermsOptions.length === 0) {
      try {
        let response = await axios.get(`${API_URL}/invoice/ddlist-terms`)
        setTerms(
          response?.data?.data.map((d: {key: string; value: string}) => ({
            value: d.key ?? '',
            label: d.value ?? '',
          })) ?? []
        )
      } catch (error) {
        console.error(error)
      }
    }
  }

  const fetchStatus = async () => {
    // Now using global cached data - this function is kept for backward compatibility
    if (globalStatusOptions.length === 0) {
      try {
        let response = await axios.get(`${API_URL}/invoice/ddlist-invoice-status`)
        if (
          currentUser?.result.userType === 'INTERPRETER' ||
          currentUser?.result.userType === 'CONSUMER'
        ) {
          setAllStatus(
            response?.data?.data
              .filter((d: any) => d.identification !== 'DR' && d.identification !== 'VO')
              .map((d: {key: string; value: string}) => ({
                value: d.key ?? '',
                label: d.value ?? '',
              })) ?? []
          )
        } else {
          setAllStatus(
            response?.data?.data.map((d: {key: string; value: string}) => ({
              value: d.key ?? '',
              label: d.value ?? '',
            })) ?? []
          )
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  const fetchTimeZones = async () => {
    try {
      let response = await axios.get(`${API_URL}/master/time-zone/active-shortlist`)
      setTimeZoneOptions(
        response?.data?.data?.map((d: any) => ({
          value: d.code,
          label: d.name,
        })) ?? []
      )
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetchInterperters()
    fetchTerms()
    fetchStatus()
    fetchTimeZones()
    if (inputRef.current) {
      inputRef.current.value = searchQuery || ''
    }
    fetchCustomerInvoice(filterData, searchQuery, currentPage, rowsPerPage)
  }, [])

  // Flatten the data to include lines
  const flattenDataWithLines = () => {
    const dataWithLines: any[] = []

    customerInvoices.forEach((header: any) => {
      if (header.lines && header.lines.length > 0) {
        header.lines.forEach((line: any) => {
          let flateData = {...header, ...line, lines: ''}
          dataWithLines.push(flateData)
        })
      } else {
        dataWithLines.push({
          ...header,
          LineCode: null,
          Interpreter: null,
          Amount: null,
        })
      }
    })

    return dataWithLines
  }

  const {onDownload} = useDownloadExcel({
    currentTableRef: tableRef.current,
    filename: 'Interpreter Invoices',
    sheet:
      filterData?.fromDate !== ''
        ? `${filterData?.fromDate} - ${filterData?.toDate}`
        : `Until ${filterData?.toDate}`,
  })

  const handleDownload = async () => {
    // Check if any filter is applied - if yes, export all matching records; if no, export current page only
    const isFilterApplied = filterData?.fromDate !== '' || filterData?.company?.length > 0 || filterData?.status?.length > 0;

    // Fetch flat data for export - backend returns data ready for Excel
    const fullResponse = await axios.post(
      `${API_URL}/invoice/overview-filter-export`,
      {
        ...filterData,
        fromDate: filterData.fromDate === '' ? null : filterData.fromDate,
        company:
          filterData.company?.length > 0
            ? filterData.company.map((d: any) => d.value.toString())
            : [],
        status: filterData.status?.length > 0 ? filterData.status.map((d: any) => d.value) : [],
        filterType: 'V',
      },
      {
        params: {
          // When filter applied: don't send pagination (backend returns all)
          // When no filter: send current page/rows to export only current view
          ...(!isFilterApplied ? { page: currentPage, items_per_page: rowsPerPage } : {}),
          ...(searchQuery?.length > 0 ? {search: searchQuery} : {}),
        },
      }
    )

    // Data is already flat from backend - one row per line
    const dataToExport = fullResponse?.data?.data ?? []

    const fieldMapping: { [key: string]: string } = {
      "Customer":                 "customerName",
      "Invoice #":                "refNo",
      "Invoice Date":             "createdDate",
      "Invoice Due Date":         "dueDate",
      "Communication Type":       "callType",
      "Interpreter":              "interpreter",
      "ReferenceID":              "refNo",
      "Appointment Id":           "appointmentId",
      "Call ID":                  "callId",
      "Record Id":                "recordId",
      "From Language":            "fromLanguage",
      "To Language":              "toLanguage",
      "Appointment Date":         "appointmentStartTime",
      "Call Date":                "callDate",
      "Scheduled Start Time":     "appointmentStartTime",
      "Scheduled End Time":       "appointmentEndtime",
      "Actual Start Time":        "osiStartTime",
      "Actual End Time":          "osiEndTime",
      "Scheduled Duration":       "duration",
      "Actual Duration":          "actualDuration",
      "Minimum Billable":         "minimumBilleble",
      "Increment":                "increment",
      "Total Billable Minuites":  "totalBillingMinutes",
      "Rate":                     "interpreterRate",
      "Total Call Amount":        "amount",
      "Travel Distance(Mile)":    "milage",
      "Mileage Rate":             "milageRate",
      "Travel Rate":              "travelRate",
      "Total Travel Charges":     "travelCharges",
      "Expenses":                 "additionalExpenses",
      "Total Mileage Expense":    "mileageExpense",
      "Travel Time Expense":      "travelCharges",
      "Extra":                    "extra",
      "Total Amount":             "lineTotal"
    };

    // 1) Build header array from mapping keys
    const header = Object.keys(fieldMapping)

    // 2) Build body rows
    const body = dataToExport.map((item: any) => {
      const row: {[col: string]: any} = {}

      header.forEach((col) => {
        const key = fieldMapping[col]
        let value = item[key]

        // Date-only columns
        if (['Invoice Date', 'Invoice Due Date', 'Appointment Date', 'Call Date'].includes(col) && value) {
          value = moment(value).format('YYYY-MM-DD')
        }

        // Time-only columns
        if (['Scheduled Start Time', 'Scheduled End Time', 'Actual Start Time', 'Actual End Time'].includes(col) && value != null) {
          if (col === 'Scheduled Start Time') {
            value = moment(item.appointmentStartTime).format('HH:mm:ss')
          } else if (col === 'Scheduled End Time') {
            value = moment(item.appointmentEndtime).format('HH:mm:ss')
          } else if (col === 'Actual Start Time') {
            value = moment(item.osiStartTime).format('HH:mm:ss')
          } else if (col === 'Actual End Time') {
            value = moment(item.osiEndTime).format('HH:mm:ss')
          }
        }

        row[col] = value
      })

      return row
    })

    // 3) Fire off the Excel download
    downloadExcel({
      fileName: 'Interpreter_Invoices',
      sheet: 'Invoice Data',
      tablePayload: {header, body},
    })
  }

  // ── Handle "+" button click — guard if job running ──────
  const handleCreateClick = () => {
    if (isJobRunning) {
      setGuardOpen(true)
    } else {
      showModal('kt_add_int-voucher')
    }
  }

  const handleGuardSelectInterpreters = () => {
    setGuardOpen(false)
    showModal('kt_add_int-voucher')
  }

  // ── Progress percentage for running banner ──────────────
  const progressPct = activeJob && activeJob.totalInterpreters > 0
    ? Math.round((activeJob.processedCount / activeJob.totalInterpreters) * 100)
    : 0

  return (
    <>
      {/* ── Guard Modal (bulk running) ───────────────────── */}
      {guardOpen && (
        <div
          onClick={() => setGuardOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(29,29,26,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, zIndex: 1060,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 420,
            boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
          }}>
            <div style={{padding: '20px 22px 18px'}}>
              <div className='d-flex align-items-center gap-2 mb-3'>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', background: '#f0e2bd',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto',
                }}>
                  <span style={{
                    width: 9, height: 9, borderRadius: '50%', background: '#a1732b',
                    animation: 'bvpulse 1.4s ease-in-out infinite', display: 'block',
                  }} />
                </div>
                <h5 className='mb-0 fw-bold' style={{fontSize: 16}}>A bulk run is already in progress</h5>
              </div>
              <p className='mb-3' style={{fontSize: 13.5, lineHeight: 1.55, color: '#55554d'}}>
                Voucher generation for all interpreters is still running. You'll get an email and a notification here when it finishes.
              </p>
              {activeJob && (
                <div style={{
                  border: '1px solid #e4e4da', borderRadius: 9, background: '#fafaf6',
                  padding: '12px 14px', fontSize: 12.5,
                }}>
                  <div className='d-flex justify-content-between mb-2'>
                    <span style={{color: '#7c7c72'}}>Period</span>
                    <span className='fw-semibold'>{formatJobPeriod(activeJob)}</span>
                  </div>
                  <div className='d-flex justify-content-between mb-2'>
                    <span style={{color: '#7c7c72'}}>Interpreters</span>
                    <span className='fw-semibold'>All {activeJob.totalInterpreters}</span>
                  </div>
                  <div className='d-flex justify-content-between mb-2'>
                    <span style={{color: '#7c7c72'}}>Last run started</span>
                    <span className='fw-semibold'>{formatJobStarted(activeJob)}</span>
                  </div>
                  <div className='d-flex justify-content-between'>
                    <span style={{color: '#7c7c72'}}>Started by</span>
                    <span className='fw-semibold'>{activeJob.requestedByName || '—'}</span>
                  </div>
                </div>
              )}
              <p className='mb-0 mt-3' style={{fontSize: 12.5, color: '#7c7c72'}}>
                You can still create a voucher for individual interpreters.
              </p>
            </div>
            <div className='d-flex justify-content-end gap-2' style={{
              padding: '13px 22px', background: '#f7f7f2', borderTop: '1px solid #e4e4da',
            }}>
              <button
                type='button'
                className='btn btn-sm'
                onClick={() => setGuardOpen(false)}
                style={{border: '1px solid #c9c9bd', background: '#fff', fontWeight: 600, color: '#3d3d37'}}
              >Close</button>
              <button
                type='button'
                className='btn btn-sm btn-primary'
                onClick={handleGuardSelectInterpreters}
                style={{fontWeight: 700}}
              >Select interpreters</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Invoice Modal ─────────────────────────── */}
      <div className='modal fade' tabIndex={-1} id='kt_add_int-voucher'>
        <div className='modal-dialog'>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={createInvoicesSchema}
            onSubmit={async (values, {setSubmitting, resetForm}) => {
              setSubmitting(true)
              try {
                const isSelectAll = values.interpreters.length === allAccounts?.length
                const response = await axios.post(`${API_URL}/invoice/voucher`, {
                  ...values,
                  interpreters: values.interpreters.map((d: any) => d.value),
                  isSelectAll,
                })

                if (response.data.status === 'BG') {
                  toaster('success', 'Bulk generation queued — we\'ll email you when it finishes.')
                  resetForm()
                  hideModal('kt_add_int-voucher')
                  // Start polling for the new job
                  fetchActiveJob()
                } else if (response.data.status === 'S') {
                  toaster('success', response.data.text ?? 'Successfully Updated')
                  resetForm()
                  hideModal('kt_add_int-voucher')
                  dispatch(InterpreterInvoiceCurrentPage(1))
                  fetchCustomerInvoice(filterData, searchQuery, 1, rowsPerPage)
                } else if (response.data.status === 'E') {
                  toaster('error', response.data.text ?? 'Updated Error')
                  if (response.data.text === 'Rates error!') {
                    setUnavailableRates(response.data.result)
                  }
                }
              } catch (ex) {
                toaster('error', 'Server Error')
                console.error(ex)
              } finally {
                setSubmitting(true)
              }
            }}
          >
            {({
              isSubmitting,
              handleChange,
              handleBlur,
              setFieldTouched,
              setFieldValue,
              handleSubmit,
              resetForm,
              values,
              errors,
              touched,
              isValid,
              dirty,
              ...formik
            }) => (
              <Form>
                <div className='modal-content'>
                  <div className='modal-header py-2'>
                    {unavailableRates?.length > 0 ? (
                      <h4 className='modal-title'>Unavailable Rates</h4>
                    ) : (
                      <h4 className='modal-title'>Create Invoice</h4>
                    )}
                    <div
                      className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                      data-bs-dismiss='modal'
                      aria-label='Close'
                      onClick={() => {
                        resetForm()
                        setUnavailableRates([])
                      }}
                    >
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr061.svg'
                        className='svg-icon svg-icon-2x'
                      />
                    </div>
                  </div>
                  <div className='modal-body'>
                    <div className='row g-4 g-xl-6'>
                      {unavailableRates?.length === 0 ? (
                        <div className='col-sm-12 col-md-12 col-lg-12'>
                          {/* ── Warning banner inside modal when bulk running ── */}
                          {isJobRunning && (
                            <div
                              className='d-flex gap-2 align-items-start mb-4'
                              style={{
                                border: '1px solid #d8c9a6', background: '#fdf8ea',
                                borderRadius: 10, padding: '12px 14px',
                              }}
                            >
                              <span style={{
                                width: 9, height: 9, borderRadius: '50%', background: '#a1732b',
                                marginTop: 5, flex: '0 0 auto',
                                animation: 'bvpulse 1.4s ease-in-out infinite',
                              }} />
                              <div style={{fontSize: 12.5, lineHeight: 1.55, color: '#5a5348'}}>
                                <strong>All interpreters is unavailable while a bulk run is in progress.</strong>{' '}
                                {activeJob && <>Last run started {formatJobStarted(activeJob)} for {formatJobPeriod(activeJob)}.</>}{' '}
                                Pick individual interpreters below, or wait for the email.
                              </div>
                            </div>
                          )}
                          <div className='mb-3'>
                            <label
                              htmlFor='exampleFormControlInput1'
                              className='required form-label fs-7'
                            >
                              Time Zone
                            </label>

                            <div className='w-100'>
                              <Select
                                className='react-select-styled react-select-solid react-select-sm custom-input-height'
                                classNamePrefix='react-select'
                                options={timezoneOptions}
                                placeholder='Select Time Zone'
                                styles={customStyles}
                                value={
                                  values.fK_TimeZoneCode === ''
                                    ? ''
                                    : timezoneOptions.find(
                                        (option: any) => option.value === values.fK_TimeZoneCode
                                      )
                                }
                                noOptionsMessage={() => 'No time zone found'}
                                onChange={(selectedOption: any) => {
                                  setFieldValue('fK_TimeZoneCode', selectedOption?.value)
                                }}
                                onBlur={(e: any) => setFieldTouched('fK_TimeZoneCode', true)}
                              />
                              {errors?.fK_TimeZoneCode && touched?.fK_TimeZoneCode && (
                                <div className='fv-plugins-message-container'>
                                  <div className='fv-help-block'>
                                    <span role='alert'>{errors.fK_TimeZoneCode}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className='mb-3'>
                            <div className='d-flex w-100 flex-1 align-items-center'>
                              <div className='col-sm-5 col-md-5 col-lg-5'>
                                <label
                                  htmlFor='exampleFormControlInput1'
                                  className='required form-label fs-7 mb-1'
                                >
                                  From
                                </label>
                                <input
                                  type='date'
                                  id='from'
                                  name='from'
                                  className='form-control form-control-white form-select-sm custom-input-height'
                                  placeholder='Enter Subject'
                                  value={values.from}
                                  onChange={(e) => setFieldValue('from', e.target.value)}
                                  onBlur={() => {
                                    setFieldTouched('from', true)
                                    setFieldTouched('to', true)
                                  }}
                                  max={new Date().toISOString().split('T')[0]}
                                />
                              </div>
                              <div className='col-sm-2 col-md-2 col-lg-2 h-100'>
                                <label
                                  htmlFor='exampleFormControlInput1'
                                  className='form-label fs-7 mb-1'
                                ></label>
                                <span className='fs-7 text-gray-600  d-flex align-items-center justify-content-center'>
                                  -
                                </span>
                              </div>
                              <div className='col-sm-5 col-md-5 col-lg-5'>
                                <label
                                  htmlFor='exampleFormControlInput1'
                                  className='required form-label fs-7 mb-1'
                                >
                                  To
                                </label>
                                <input
                                  type='date'
                                  id='to'
                                  name='to'
                                  className='form-control form-control-white form-select-sm custom-input-height'
                                  placeholder='Start Time'
                                  value={values.to}
                                  onChange={(e) => setFieldValue('to', e.target.value)}
                                  onBlur={() => {
                                    setFieldTouched('from', true)
                                    setFieldTouched('to', true)
                                  }}
                                  max={new Date().toISOString().split('T')[0]}
                                />
                              </div>
                            </div>
                            {((errors.from && touched?.from) || (errors.to && touched?.to)) && (
                              <div className='fv-plugins-message-container mx-1'>
                                {errors.from && touched?.from && (
                                  <div className='fv-help-block'>{String(errors?.from)}</div>
                                )}
                                {errors?.to && touched?.to && (
                                  <div className='fv-help-block'>{String(errors?.to)}</div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className='mb-3'>
                            <div className='row d-flex mb-3'>
                              <div className='col-md-12'>
                                <div className='d-flex justify-content-between align-items-center'>
                                  <div>
                                    <label
                                      htmlFor='exampleFormControlInput1'
                                      className='required form-label fs-7 mb-1'
                                    >
                                      Interperter(s)
                                    </label>
                                  </div>
                                  <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                    <input
                                      className='form-check-input'
                                      type='checkbox'
                                      checked={values?.interpreters?.length === allAccounts?.length}
                                      disabled={isJobRunning}
                                      title={isJobRunning ? 'Unavailable while a bulk run is in progress' : 'Select all interpreters'}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setFieldValue('interpreters', allAccounts)
                                        } else {
                                          setFieldValue('interpreters', [])
                                        }
                                      }}
                                      style={isJobRunning ? {cursor: 'not-allowed', opacity: 0.5} : {}}
                                    />
                                    <label
                                      className='form-check-label'
                                      htmlFor='flexRadioLg1'
                                      style={isJobRunning ? {color: '#a4a49b', cursor: 'not-allowed'} : {}}
                                    >
                                      All
                                    </label>
                                  </div>
                                </div>
                                <Select
                                  {...formik.getFieldProps('interpreters')}
                                  isMulti
                                  className='react-select-styled react-select-solid react-select-sm'
                                  classNamePrefix='react-select'
                                  options={allAccounts}
                                  placeholder='Select Interperter'
                                  styles={customStyles}
                                  value={values.interpreters}
                                  onChange={(selectedOption: any) => {
                                    setFieldValue('interpreters', selectedOption)
                                  }}
                                  onBlur={(e: any) => setFieldTouched('interpreters', true)}
                                />
                                {errors?.interpreters && touched?.interpreters && (
                                  <div className='fv-plugins-message-container'>
                                    <div className='fv-help-block'>
                                      <span role='alert'>{String(errors?.interpreters ?? '')}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className='mb-3 row'>
                            <div className='col-md-6'>
                              <label
                                htmlFor='exampleFormControlInput1'
                                className='required form-label fs-7 mb-1'
                              >
                                Terms
                              </label>
                              <Select
                                className='react-select-styled react-select-solid react-select-sm'
                                classNamePrefix='react-select'
                                options={terms}
                                placeholder='Select Term'
                                onChange={(option: {value: any}) =>
                                  setFieldValue('term', option?.value || '')
                                }
                                value={
                                  values.term === ''
                                    ? ''
                                    : terms?.find((option) => option.value === values.term)
                                }
                                onBlur={(e: any) => setFieldTouched('term', true)}
                                styles={{
                                  control: (provided: any) => ({
                                    ...provided,
                                    width: '100%',
                                    border: '1px solide4e6ef',
                                  }),
                                }}
                              />
                              {errors?.term && touched?.term && (
                                <div className='fv-plugins-message-container'>
                                  <div className='fv-help-block'>
                                    <span role='alert'>{String(errors?.term ?? '')}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className='col-md-6'>
                              <label
                                htmlFor='exampleFormControlInput1'
                                className='required form-label fs-7 mb-1'
                              >
                                Invoice Date
                              </label>
                              <input
                                type='date'
                                id='voucherDate'
                                name='voucherDate'
                                className='form-control form-control-white form-select-sm custom-input-height'
                                placeholder='Enter Invoice Date'
                                value={values.voucherDate}
                                onChange={(e) => setFieldValue('voucherDate', e.target.value)}
                                onBlur={() => {
                                  setFieldTouched('voucherDate', true)
                                }}
                                max={new Date().toISOString().split('T')[0]}
                              />
                              {errors?.voucherDate && touched?.voucherDate && (
                                <div className='fv-plugins-message-container'>
                                  <div className='fv-help-block'>
                                    <span role='alert'>{String(errors?.voucherDate ?? '')}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className='mb-3'>
                            <div className='row d-flex mb-3'>
                              <div className='col-md-6'>
                                <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                  <input
                                    className='form-check-input'
                                    type='checkbox'
                                    value=''
                                    id='isIncludeCalls'
                                    name='isIncludeCalls'
                                    checked={values?.isIncludeCalls}
                                    onChange={(e) =>
                                      setFieldValue('isIncludeCalls', e.target.checked)
                                    }
                                    onBlur={handleBlur}
                                  />
                                  <label className='form-check-label' htmlFor='flexRadioLg1'>
                                    Include Calls
                                  </label>
                                </div>
                              </div>
                              <div className='col-md-6'>
                                <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                  <input
                                    className='form-check-input'
                                    type='checkbox'
                                    value=''
                                    id='isIncludeAppointments'
                                    name='isIncludeAppointments'
                                    checked={values?.isIncludeAppointments}
                                    onChange={(e) =>
                                      setFieldValue('isIncludeAppointments', e.target.checked)
                                    }
                                    onBlur={handleBlur}
                                  />
                                  <label className='form-check-label' htmlFor='flexRadioLg1'>
                                    Include Appointments
                                  </label>
                                </div>
                              </div>
                            </div>
                            {errors?.isIncludeCalls && touched?.isIncludeCalls && (
                              <div className='fv-plugins-message-container'>
                                <div className='fv-help-block'>
                                  <span role='alert'>{String(errors?.isIncludeCalls ?? '')}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className='table-responsive'>
                          <div className='card'>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-2 gy-2 table-hover'>
                              <thead>
                                <tr className='fw-semibold text-muted text-uppercase'>
                                  <th className='min-w-150px '>Interperter</th>
                                  <th className='min-w-200px '>Communication Type</th>
                                  <th className='min-w-150px '>Service Type</th>
                                  <th className='min-w-200px '>Language</th>
                                  <th className='min-w-150px '>Day</th>
                                  <th className='min-w-100px text-end'>call Time</th>
                                </tr>
                              </thead>
                              <tbody>
                                {unavailableRates?.map((item: any, index: any) => (
                                  <tr>
                                    <td>
                                      <div className='d-flex justify-content-start flex-column'>
                                        <a className='text-gray-800 text-hover-primary fs-6'>
                                          {item?.interpreter ?? '-'}
                                        </a>
                                      </div>
                                    </td>
                                    <td>
                                      <div className='d-flex justify-content-start flex-column'>
                                        <a className='text-gray-800 text-hover-primary fs-6'>
                                          {item?.communicationType ?? '-'}
                                        </a>
                                      </div>
                                    </td>
                                    <td>
                                      <div className='d-flex justify-content-start flex-column'>
                                        <a className='text-gray-800 text-hover-primary fs-6'>
                                          {item?.serviceType ?? '-'}
                                        </a>
                                      </div>
                                    </td>

                                    <td>
                                      <div className='d-flex justify-content-start flex-column'>
                                        <a className='text-gray-800 text-hover-primary fs-6'>
                                          {item?.fromLanguage ?? '-'} - {item?.toLanguage ?? '-'}
                                        </a>
                                      </div>
                                    </td>

                                    <td>
                                      <div className='d-flex justify-content-start flex-column'>
                                        <a className='text-gray-800 text-hover-primary fs-6'>
                                          {item?.day ?? '-'}
                                        </a>
                                      </div>
                                    </td>
                                    <td>
                                      <div className='d-flex justify-content-start flex-column text-end'>
                                        <a className='text-gray-800 text-hover-primary fs-6'>
                                          {item?.OSIStartTime ?? '-'}
                                        </a>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              {/* end::Table body */}
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className='separator my-3'></div>
                    {unavailableRates?.length === 0 && (
                      <div className='d-flex flex-column gap-2'>
                        <span className='fs-8 fst-italic'>
                          Note: appointments with signatures still pending will not be included.
                        </span>
                        <div className='d-flex align-items-center gap-2'>
                          <label
                            htmlFor='isIndividualInvoice'
                            className='mb-0 cursor-pointer fw-semibold'
                            style={{color: '#d9214e', fontSize: '11.5px'}}
                          >
                            Generate Individual Invoice
                          </label>
                          <OverlayTrigger
                            placement='top'
                            overlay={
                              <Tooltip id='tooltip-individual-invoice'>
                                If on, invoices will generate based on individual calls/appointments and will not be merged into one.
                              </Tooltip>
                            }
                          >
                            <span className='cursor-pointer d-inline-flex align-items-center' tabIndex={0}>
                              <i className='bi bi-info-circle text-gray-700' style={{fontSize: '11px'}}></i>
                            </span>
                          </OverlayTrigger>
                          <div className='form-check form-switch form-check-custom form-check-solid form-check-success mb-0 ms-1'>
                            <input
                              className='form-check-input cursor-pointer'
                              type='checkbox'
                              role='switch'
                              id='isIndividualInvoice'
                              name='isIndividualInvoice'
                              style={{height: '18px', width: '32px'}}
                              checked={values?.isIndividualInvoice}
                              onChange={(e) => setFieldValue('isIndividualInvoice', e.target.checked)}
                              onBlur={handleBlur}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {unavailableRates?.length === 0 && (
                    <div className='modal-footer py-3'>
                      <button
                        type='button'
                        className='btn btn-light btn-sm'
                        data-bs-dismiss='modal'
                        onClick={() => {
                          resetForm()
                          setUnavailableRates([])
                        }}
                      >
                        Close
                      </button>

                      <button
                        type='button'
                        className='btn btn-primary btn-sm'
                        onClick={() => handleSubmit()}
                        disabled={isSubmitting || !dirty}
                      >
                        Create Invoice
                      </button>
                    </div>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <div className='card-body p-0' style={{minHeight: '70vh'}}>
        <div className='d-flex justify-content-between mb-3 align-items-center flex-wrap'>
          <div className='card-title d-flex align-items-center me-4 mb-3'>
            <div className='d-flex flex-column'>
              <div className='d-flex align-items-center'>
                {currentUser?.result?.userType === 'SYSTEM' ? (
                  <h5 className='text-black fs-4 fw-semibold mb-0'>Interpreter Invoices</h5>
                ) : (
                  <h5 className='text-black fs-4 fw-semibold mb-0'>My Invoices</h5>
                )}
              </div>
              <div className='d-flex flex-wrap fs-6 '>
                {currentUser?.result?.userType === 'SYSTEM' ? (
                  <p className='text-gray-500 mb-0 fw-normal' style={{fontSize: '12px'}}>
                    Manage Interpreter Invoices
                  </p>
                ) : (
                  <p className='text-gray-500 mb-0 fw-normal' style={{fontSize: '12px'}}>
                    Manage My Invoices
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className='d-flex'>
            <div className='d-flex flex-wrap'>
              <div className='d-flex align-items-center position-relative me-3 flex-nowrap'>
                <input
                  ref={inputRef}
                  type='text'
                  data-kt-user-table-filter='search'
                  className='form-control form-control-white form-control-sm max-w-250px custom-search-radius'
                  placeholder='Search'
                  onKeyDown={handleKeyDown}
                  onChange={() => {
                    const value = inputRef.current?.value || ''
                    if (value === '') {
                      dispatch(InterpreterInvoiceSearch(''))
                      dispatch(InterpreterInvoiceCurrentPage(1))
                      fetchCustomerInvoice(filterData, '', 1, rowsPerPage)
                    }
                  }}
                />

                <button
                  type='button'
                  className='btn btn-primary btn-icon btn-sm custom-search-btn-radius px-3'
                  onClick={() => {
                    const searchValue = inputRef.current?.value || ''
                    dispatch(InterpreterInvoiceSearch(searchValue))
                    dispatch(InterpreterInvoiceCurrentPage(1))
                    fetchCustomerInvoice(filterData, {search: searchValue}, 1, rowsPerPage)
                  }}
                >
                  <KTSVG path='/media/icons/duotune/general/gen021.svg' className='' />
                </button>
              </div>
              <div className='my-0 me-3' style={{display: 'flex', justifyContent: 'end'}}>
                <BillingInterpreterFilterDropdown
                  allAccounts={allAccounts}
                  filterData={filterData}
                  setCurrentPage={currentPage}
                  allStatus={allStatus}
                  fetchCustomerInvoice={fetchCustomerInvoice}
                  searchQuery={searchQuery}
                  setRowsPerPage={rowsPerPage}
                />
              </div>
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip id='tooltip-filter'>Download CSV vf</Tooltip>}
              >
                <button
                  type='button'
                  className='btn btn-primary btn-sm me-3 btn-icon'
                  onClick={handleDownload}
                >
                  <KTSVG path='/media/icons/duotune/files/fil017.svg' className='svg-icon-muted' />
                </button>
              </OverlayTrigger>
              {currentUser?.result?.userType === 'SYSTEM' && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='tooltip-filter'>
                      {isJobRunning ? 'A bulk run is already in progress' : 'Create Invoice'}
                    </Tooltip>
                  }
                >
                  <div style={{position: 'relative'}}>
                    <button
                      type='button'
                      className='btn btn-sm btn-primary btn-icon'
                      onClick={handleCreateClick}
                    >
                      <i className='bi bi-plus fs-2'></i>
                    </button>
                    {/* Amber dot indicator when bulk is running */}
                    {isJobRunning && (
                      <span
                        style={{
                          position: 'absolute', top: -3, right: -3,
                          width: 9, height: 9, borderRadius: '50%',
                          background: '#a1732b', border: '2px solid #f2f2ee',
                        }}
                      />
                    )}
                  </div>
                </OverlayTrigger>
              )}
            </div>
          </div>
        </div>

        {/* ── Running Banner ─────────────────────────────── */}
        {isJobRunning && activeJob && (
          <div
            className='mb-4'
            style={{
              border: '1px solid #d8c9a6', background: '#fdf8ea',
              borderRadius: 12, padding: '18px 20px',
              display: 'grid', gridTemplateColumns: '34px 1fr auto',
              gap: 16, alignItems: 'start',
            }}
          >
            <div style={{
              width: 34, height: 34, borderRadius: '50%', background: '#f0e2bd',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%', background: '#a1732b',
                animation: 'bvpulse 1.4s ease-in-out infinite', display: 'block',
              }} />
            </div>
            <div style={{minWidth: 0}}>
              <div className='d-flex align-items-center gap-2 flex-wrap mb-1'>
                <h6 className='mb-0 fw-bold' style={{fontSize: 15.5}}>Bulk voucher generation is running</h6>
                <span style={{
                  padding: '3px 9px', borderRadius: 999, background: '#f0e2bd',
                  color: '#7a5a12', fontSize: 10.5, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>All interpreters</span>
              </div>
              <p className='mb-3' style={{fontSize: 13.5, lineHeight: 1.55, color: '#5a5348', maxWidth: 720}}>
                You don't need to stay on this page. We'll email you and post a notification here when it finishes.
              </p>
              {/* Progress bar */}
              <div style={{
                height: 4, borderRadius: 999, background: '#f0e2bd',
                overflow: 'hidden', maxWidth: 720, marginBottom: 12,
              }}>
                <div style={{
                  width: `${progressPct}%`, height: '100%', borderRadius: 999,
                  background: '#a1732b', transition: 'width 0.5s ease',
                }} />
              </div>
              <div className='d-flex flex-wrap gap-4' style={{fontSize: 12.5}}>
                <div><span style={{color: '#8a8375'}}>Period </span><span className='fw-semibold'>{formatJobPeriod(activeJob)}</span></div>
                <div><span style={{color: '#8a8375'}}>Interpreters </span><span className='fw-semibold'>All {activeJob.totalInterpreters}</span></div>
                <div><span style={{color: '#8a8375'}}>Started </span><span className='fw-semibold'>{formatJobStarted(activeJob)}</span></div>
                <div><span style={{color: '#8a8375'}}>By </span><span className='fw-semibold'>{activeJob.requestedByName || '—'}</span></div>
              </div>
            </div>
            <div>
              <button
                type='button'
                className='btn btn-sm'
                disabled={checkingStatus}
                onClick={() => void handleCheckBulkJobStatus()}
                style={{
                  border: '1px solid #c9b489', background: '#fff', color: '#7a5a12',
                  fontWeight: 600, whiteSpace: 'nowrap', fontSize: 12.5,
                  minWidth: 100,
                }}
              >{checkingStatus ? (
                <span className='spinner-border spinner-border-sm' role='status' aria-label='Loading' />
              ) : 'Check status'}</button>
            </div>
          </div>
        )}

        {/* ── Completed Banner ───────────────────────────── */}
        {isJobDone && activeJob && (
          <div
            className='mb-4'
            style={{
              border: '1px solid #c8d1b4', background: '#f4f7ea',
              borderRadius: 12, padding: '16px 20px',
              display: 'grid', gridTemplateColumns: '34px 1fr auto',
              gap: 16, alignItems: 'center',
            }}
          >
            <div style={{
              width: 34, height: 34, borderRadius: '50%', background: '#e0e7cc',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, color: '#4f6220',
            }}>&#10003;</div>
            <div>
              <h6 className='mb-1 fw-bold' style={{fontSize: 15.5}}>
                Bulk run finished — {activeJob.totalLinesCreated} vouchers created
              </h6>
              <p className='mb-0' style={{fontSize: 13.5, color: '#55604a'}}>
                {formatJobPeriod(activeJob)} &middot; All {activeJob.totalInterpreters} &middot; {activeJob.skippedCount} skipped for missing rates. A copy of the summary is in your inbox.
              </p>
            </div>
            <div className='d-flex gap-2'>
              <button
                type='button'
                className='btn btn-sm'
                onClick={() => {
                  setDoneDismissed(true)
                  // Refresh the table since new vouchers were created
                  dispatch(InterpreterInvoiceCurrentPage(1))
                  fetchCustomerInvoice(filterData, searchQuery, 1, rowsPerPage)
                }}
                style={{
                  border: '1px solid #bcc7a4', background: '#fff', color: '#4f6220',
                  fontWeight: 600, whiteSpace: 'nowrap', fontSize: 12.5,
                }}
              >Dismiss</button>
            </div>
          </div>
        )}

        {/* begin::Table container */}
        <div className='table-responsive'>
          {/* begin::Table */}
          <InterpreterVoucherTable
            className='table align-middle table-row-dashed fs-6 gy-5'
            isLoading={isLoading}
            data={customerInvoices}
            tableRef={tableRef}
          />
          {/* end::Table */}
        </div>
        <CommonPaginationModel
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          total={totalItems}
          onPageChange={(newPage: number) => {
            dispatch(InterpreterInvoiceCurrentPage(newPage))
            fetchCustomerInvoice(filterData, searchQuery, newPage, rowsPerPage)
          }}
          onRowsPerPageChange={(newRowsPerPage: number) => {
            dispatch(InterpreterInvoiceRowsPerPage(newRowsPerPage))
            dispatch(InterpreterInvoiceCurrentPage(1))
            fetchCustomerInvoice(filterData, searchQuery, 1, newRowsPerPage)
          }}
        />

        {/* end::Table container */}
      </div>

      {/* ── CSS keyframes for pulse animation ────────────── */}
      <style>{`
        @keyframes bvpulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
      `}</style>
    </>
  )
}
